import { Capacitor } from '@capacitor/core';

/**
 * On iOS, WKWebView's GPU rendering process is permanently destroyed when any
 * modal (Camera picker, file input) is presented on top of it. To avoid this,
 * we open the image picker in a SEPARATE UIWindow on the native side so the
 * WebView is never covered. The native side handles image selection and returns
 * base64 data via a JS callback, completely bypassing WKWebView's broken file input.
 */
export function installNativeFilePicker() {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

  document.addEventListener('click', handleFileInputClick, true);

  const originalClick = HTMLInputElement.prototype.click;
  HTMLInputElement.prototype.click = function (this: HTMLInputElement) {
    if (this.type === 'file') {
      const accept = this.accept || '';
      if (!accept.includes('video')) {
        pickImageNative(this);
        return;
      }
    }
    originalClick.call(this);
  };
}

function handleFileInputClick(e: Event) {
  const target = e.target as HTMLElement;

  let input: HTMLInputElement | null = null;

  if (target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'file') {
    input = target as HTMLInputElement;
  } else {
    const label = target.closest('label');
    if (label?.htmlFor) {
      const linked = document.getElementById(label.htmlFor);
      if (linked?.tagName === 'INPUT' && (linked as HTMLInputElement).type === 'file') {
        input = linked as HTMLInputElement;
      }
    }
    if (!input) {
      const parent = target.closest('[onclick], button, label, div');
      if (parent) {
        const child = parent.querySelector('input[type="file"]') as HTMLInputElement | null;
        if (child) input = child;
      }
    }
  }

  if (!input) return;

  const accept = input.accept || '';
  if (accept.includes('video')) return;

  e.preventDefault();
  e.stopPropagation();
  e.stopImmediatePropagation();

  pickImageNative(input);
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const byteChars = atob(base64);
  const byteArrays: Uint8Array[] = [];
  for (let offset = 0; offset < byteChars.length; offset += 512) {
    const slice = byteChars.slice(offset, offset + 512);
    const byteNumbers = new Uint8Array(slice.length);
    for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
    byteArrays.push(byteNumbers);
  }
  return new Blob(byteArrays, { type: mimeType });
}

async function pickImageNative(input: HTMLInputElement) {
  try {
    // Ask native to open picker in a separate window (doesn't freeze the WebView)
    const base64 = await new Promise<string | null>((resolve) => {
      (window as any)._nativePickerResolve = resolve;
      try {
        (window as any).webkit.messageHandlers.pickImageNative.postMessage('pick');
      } catch {
        resolve(null);
      }
      // Timeout after 60 seconds
      setTimeout(() => resolve(null), 60000);
    });

    if (!base64) return;

    const blob = base64ToBlob(base64, 'image/jpeg');
    const file = new File([blob], `photo_${Date.now()}.jpeg`, { type: 'image/jpeg' });

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    input.files = dataTransfer.files;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  } catch (err) {
    console.error('Native image picker error:', err);
  }
}
